/**
 * Reactive layer over the Harness FME (Split) client.
 *
 * The Split SDK is not reactive and is asynchronous (treatments are `"control"`
 * until `SDK_READY`). This module exposes a Vue-reactive read so flag consumers
 * (`useFlag`) re-evaluate once the SDK becomes ready or a treatment changes at
 * runtime (`SDK_UPDATE`), without every consumer wiring up SDK events itself.
 */
import { ref } from "vue";
import type { FlagName } from "@/config/flagsConfig";
import {
  getSplitClient,
  getTreatment,
  initSplitClient,
  type SplitClientConfig,
} from "./splitClient";

// Bumped whenever Harness treatments may have changed. Reading it inside a
// computed registers a dependency, so the computed re-runs on each bump.
const harnessVersion = ref(0);

// The client instance we have already attached listeners to, so re-keying
// (a new client) re-subscribes while repeat calls for the same client no-op.
let subscribedClient: SplitIO.IBrowserClient | null = null;

const bumpVersion = () => {
  harnessVersion.value += 1;
};

/**
 * Attaches SDK listeners so reactive reads refresh. Safe to call repeatedly;
 * only (re)subscribes when the underlying client instance changes.
 */
export const subscribeHarnessUpdates = (): void => {
  const client = getSplitClient();
  if (!client || client === subscribedClient) return;

  subscribedClient = client;
  // `on(SDK_READY)` does not fire if the SDK is already ready, so use ready()
  // which resolves immediately in that case.
  client.ready().then(bumpVersion).catch(bumpVersion);
  client.on(client.Event.SDK_UPDATE, bumpVersion);
};

/**
 * Reactive treatment read for a flag. Yields `"control"` until the SDK is
 * ready. Must be called inside a reactive effect/computed to track updates.
 */
export const getHarnessTreatment = (flagName: FlagName): SplitIO.Treatment => {
  // Register the reactive dependency (value intentionally unused).
  void harnessVersion.value;
  return getTreatment(flagName);
};

/** Initialises the Split client and subscribes to its updates. */
export const bootstrapHarnessFlags = (config: SplitClientConfig): void => {
  initSplitClient(config);
  subscribeHarnessUpdates();
};

export const resetHarnessFlagsForTests = (): void => {
  subscribedClient = null;
  harnessVersion.value = 0;
};
